/**
 * Defense Audio System — Web Audio API sound functions for threat alerts.
 *
 * Named sound functions for each defense event type.
 * Uses shared AudioContext from audioContext.ts.
 */

import { playTone } from './audioContext';

/** Single ping — threat escalation to caution */
export function playCautionTone(): void {
  playTone(600, 0.15, 0.3);
}

/** Double ping — threat escalation to danger */
export function playDangerTone(): void {
  playTone(800, 0.1, 0.3);
  setTimeout(() => playTone(800, 0.1, 0.3), 150);
}

/** Soft relief tone — position exits danger */
export function playReliefTone(): void {
  playTone(400, 0.2, 0.15);
}

/** Triple staccato — ripcord executed */
export function playRipcordTone(): void {
  playTone(1000, 0.05, 0.3);
  setTimeout(() => playTone(1000, 0.05, 0.3), 100);
  setTimeout(() => playTone(1000, 0.05, 0.3), 200);
}

/** Rising sweep — liquidity sweep detected */
export function playSweepTone(): void {
  playTone(400, 0.12, 0.25);
  setTimeout(() => playTone(600, 0.12, 0.25), 130);
  setTimeout(() => playTone(900, 0.15, 0.3), 260);
}

/** Low pulse — exposure limit breached */
export function playExposureBreachTone(): void {
  playTone(300, 0.2, 0.35);
  setTimeout(() => playTone(300, 0.2, 0.35), 250);
}

/** Descending warble — adverse flow pattern escalation */
export function playAdverseFlowTone(): void {
  playTone(700, 0.1, 0.25);
  setTimeout(() => playTone(550, 0.1, 0.25), 120);
  setTimeout(() => playTone(400, 0.15, 0.3), 240);
}

/** Ascending two-note chime — order reached front of queue */
export function playQueueFrontTone(): void {
  playTone(1200, 0.12, 0.15);
  setTimeout(() => playTone(1600, 0.15, 0.2), 120);
}

/** Triple ascending chirp — order at best price */
export function playBestPriceTone(): void {
  playTone(800, 0.1, 0.12);
  setTimeout(() => playTone(1000, 0.1, 0.12), 100);
  setTimeout(() => playTone(1200, 0.12, 0.15), 200);
}

/** Boot sound — slow power-up escalation + solid confirmation ding (~1s total)
 *  Phase 1: low rumble building up (150→300Hz, quiet)
 *  Phase 2: mid tone steps up (400Hz, 500Hz)
 *  Phase 3: bright confirmation ding that rings out (800Hz, sustained)
 */
export function playBootTone(): void {
  // Phase 1 — sub-bass rumble, felt more than heard
  playTone(150, 0.25, 0.1);
  setTimeout(() => playTone(200, 0.2, 0.12), 150);
  setTimeout(() => playTone(300, 0.15, 0.15), 300);
  // Phase 2 — mid climb, building momentum
  setTimeout(() => playTone(400, 0.12, 0.18), 430);
  setTimeout(() => playTone(500, 0.1, 0.2), 530);
  // Phase 3 — the ding: bright, sustained, "locked in"
  setTimeout(() => playTone(800, 0.4, 0.3), 650);
}
